import moment from "moment";
import {
  ReceivableItem,
  CnCOrderReceivableItem,
  RFZOTherapyReceivableItem,
} from "../types/receivable";
import { CnCOrderResponse, RFZOTherapyResponse } from "../types/api";

/**
 * Sorts receivable items by created timestamp in descending order (newest first)
 */
export const sortReceivableItemsByCreated = (
  items: ReceivableItem[]
): ReceivableItem[] => {
  return [...items].sort((a, b) => {
    const momentA = moment(a.created);
    const momentB = moment(b.created);

    // Return newest first (descending order)
    return momentB.valueOf() - momentA.valueOf();
  });
};

/**
 * Filters RFZO requests to only include those with 'REQUESTED' status
 */
export const getRequestedRFZORequests = (
  rfzoRequests: RFZOTherapyResponse[]
): RFZOTherapyResponse[] => {
  return rfzoRequests.filter((request) => request.status === "REQUESTED");
};

/**
 * Combines orders and RFZO requests into a unified, sorted array
 */
export const combineAndSortReceivableItems = (
  orders: CnCOrderResponse[],
  rfzoRequests: RFZOTherapyResponse[]
): ReceivableItem[] => {
  // Filter RFZO requests to only include 'REQUESTED' status
  const requestedRfzoRequests = getRequestedRFZORequests(rfzoRequests);

  // Transform to receivable items with discriminator
  const orderItems: CnCOrderReceivableItem[] = orders.map((order) => ({
    ...order,
    itemType: "ORDER" as const,
  }));

  const rfzoItems: RFZOTherapyReceivableItem[] = requestedRfzoRequests.map(
    (rfzo) => ({
      ...rfzo,
      itemType: "RFZO_REQUEST" as const,
    })
  );

  // Combine and sort
  const combinedItems: ReceivableItem[] = [...orderItems, ...rfzoItems];

  return sortReceivableItemsByCreated(combinedItems);
};

/**
 * Calculates the oldest order creation time for countdown timer
 * (keeping existing logic from ReceivedColumn)
 */
export const calculateOldestOrderTime = (
  orders: CnCOrderResponse[]
): number | null => {
  if (orders.length === 0) return null;

  if (orders.length === 1) {
    return new Date(orders[0].created).getTime();
  }

  return orders.reduce((oldestTime, order) => {
    const orderTime = new Date(order.created).getTime();
    return orderTime < oldestTime ? orderTime : oldestTime;
  }, new Date().getTime());
};
